import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductVariant
 * @property {string} id
 * @property {string} title
 * @property {string} option1
 * @property {string} option2
 * @property {string} option3
 * @property {boolean} available
 */

const TINT_VARIANT_SELECTORS = {
  variantPicker: 'variant-picker',
  variantsListScript: 'script[data-variants-list]',
  tintColorPickerRoot: 'tint-color-picker',
  addToCartComponent: 'add-to-cart-component',
  addToCartButtonRef: '[ref="addToCartButton"]',
  addToCartFallbackSubmit: 'button[type="submit"]',
  addToCartFallbackButton: 'button',
};

const TINT_VARIANT_EVENTS = {
  colorConfirmed: 'color_selection_confirmed',
  colorCleared: 'color_selection_cleared',
  variantUpdate: ThemeEvents.variantUpdate,
};

const TINT_VARIANT_POSITIONS = {
  canSize: 1,
  base: 2,
  tint: 3,
};

const TINT_VARIANT_BASE_VALUES = {
  undyedDefault: 'w',
};

const TINT_VARIANT_ATTRIBUTES = {
  colorRequired: 'data-color-required',
};

const TINT_VARIANT_FLAGS = {
  enableColourCrushImageOverride: 'data-enable-colour-crush-image-override',
};

const COLOR_IMAGE_DATA = {
  originalSrc: 'data-color-original-src',
  originalSrcset: 'data-color-original-srcset',
  active: 'data-color-image-active',
};

class ColorVariantManager extends Component {
  constructor() {
    super();
    this.selectedColorDetail = null;
    this.pendingVariantChange = false;
    this.enableImageOverride = false;

    /** @type {MutationObserver | null} */
    this.imageObserver = null;

    /** @type {number | null} */
    this.reapplyTimeoutId = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.pickerRoot = this.closest(TINT_VARIANT_SELECTORS.tintColorPickerRoot);
    this.sectionRoot = this.closest('.shopify-section') || document;

    this.enableImageOverride = Boolean(
      this.pickerRoot && this.pickerRoot.hasAttribute(TINT_VARIANT_FLAGS.enableColourCrushImageOverride)
    );
    
    this.bindEvents();
    if (this.enableImageOverride) this.startImageOverrideObserver();
    
    // Set initial color picker visibility and button state based on current Tint selection
    this.updateColorPickerVisibility();
    this.updateAddToCartButtonState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopImageOverrideObserver();
  }
  
  getTints(attr, fallback) {
    if (!this.pickerRoot) return [fallback];
    return (this.pickerRoot.getAttribute(attr) || fallback)
      .split(',')
      .map((v) => v.toLowerCase().trim())
      .filter(Boolean);
  }

  isDyedTintValue(value) {
    if (!value) return false;
    const lower = value.toLowerCase();
    const dyedList = this.getTints('data-dyed-tint-values', 'dyed');
    return dyedList.includes(lower);
  }

  isUndyedTintValue(value) {
    if (!value) return false;
    const lower = value.toLowerCase();
    const undyedList = this.getTints('data-undyed-tint-values', 'undyed');
    return undyedList.includes(lower);
  }

  bindEvents() {
    if (this.pickerRoot) {
      this.pickerRoot.addEventListener(TINT_VARIANT_EVENTS.colorConfirmed, this.handleColorSelection.bind(this));
      this.pickerRoot.addEventListener(TINT_VARIANT_EVENTS.colorCleared, this.handleColorCleared.bind(this));
    }
    
    if (this.sectionRoot) {
      this.sectionRoot.addEventListener('change', this.handleChange.bind(this));
      this.sectionRoot.addEventListener(TINT_VARIANT_EVENTS.variantUpdate, this.handleVariantUpdate.bind(this));
    }
  }

  getVariantPicker() {
    return this.sectionRoot.querySelector(TINT_VARIANT_SELECTORS.variantPicker);
  }

  getVariantsMatrix() {
    const script = this.pickerRoot?.querySelector(TINT_VARIANT_SELECTORS.variantsListScript) || 
                   this.sectionRoot.querySelector(TINT_VARIANT_SELECTORS.variantsListScript);
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (e) {
      return [];
    }
  }

  getSelectedValue(position) {
    const picker = this.getVariantPicker();
    if (!picker) return null;
    
    const inputs = Array.from(picker.querySelectorAll(`[data-input-id^="${position}-"]`));
    for (const input of inputs) {
      if (input instanceof HTMLInputElement && input.checked) {
        return input.value;
      }
      if (input instanceof HTMLOptionElement && input.selected) {
        return input.value;
      }
    }

    if (inputs.length === 0) {
      const variantOptions = picker.querySelectorAll('.variant-option');
      const optionEl = variantOptions[position - 1];
      if (optionEl) {
        const singleValueEl = optionEl.querySelector('[data-testid="variant-option-single-value"]');
        if (singleValueEl) {
          return singleValueEl.textContent.trim();
        }
      }
    }

    return null;
  }

  updateColorPickerVisibility() {
    if (!this.pickerRoot) return;

    const currentTint = this.getSelectedValue(TINT_VARIANT_POSITIONS.tint) || '';
    const isDyed = this.isDyedTintValue(currentTint);

    if (isDyed) {
      this.pickerRoot.removeAttribute('hidden');
    } else {
      this.pickerRoot.setAttribute('hidden', '');
    }
  }

  getAddToCartButton() {
    const addToCartComponent = this.sectionRoot.querySelector(TINT_VARIANT_SELECTORS.addToCartComponent);
    if (!addToCartComponent) return null;
    
    const button = addToCartComponent.querySelector(TINT_VARIANT_SELECTORS.addToCartButtonRef) ||
                   addToCartComponent.querySelector(TINT_VARIANT_SELECTORS.addToCartFallbackSubmit) ||
                   addToCartComponent.querySelector(TINT_VARIANT_SELECTORS.addToCartFallbackButton);
    
    return /** @type {HTMLButtonElement | null} */ (button);
  }

  updateAddToCartButtonState() {
    const button = this.getAddToCartButton();
    if (!button) return;

    if (this.pendingVariantChange) {
      button.disabled = true;
      button.setAttribute(TINT_VARIANT_ATTRIBUTES.colorRequired, 'true');
      return;
    }

    const currentTint = this.getSelectedValue(TINT_VARIANT_POSITIONS.tint) || '';
    const isDyed = this.isDyedTintValue(currentTint);
    const hasColorSelected = !!this.selectedColorDetail;
    const currentCanSize = this.getSelectedValue(TINT_VARIANT_POSITIONS.canSize) || '';
    const currentBase = this.getSelectedValue(TINT_VARIANT_POSITIONS.base) || '';

    if (isDyed && !hasColorSelected) {
      button.disabled = true;
      button.setAttribute(TINT_VARIANT_ATTRIBUTES.colorRequired, 'true');
    } else if (isDyed && hasColorSelected) {
      button.removeAttribute(TINT_VARIANT_ATTRIBUTES.colorRequired);
      const variants = this.getVariantsMatrix();

      const currentCanSizeLower = currentCanSize.toLowerCase();
      const currentTintLower = currentTint.toLowerCase();

      const requiredBaseFromColor = (this.selectedColorDetail?.colorBase || '').toLowerCase();
      const effectiveBaseLower = (currentBase || requiredBaseFromColor).toLowerCase();

      const currentVariant = variants.find((/** @type {ProductVariant} */ v) =>
        (v.option1 || '').toLowerCase() === currentCanSizeLower &&
        (v.option2 || '').toLowerCase() === effectiveBaseLower &&
        (v.option3 || '').toLowerCase() === currentTintLower
      );

      if (currentVariant && currentVariant.available) {
        button.disabled = false;
      } else {
        button.disabled = true;
      }
    } else if (!isDyed) {
      button.removeAttribute('data-color-required');
    }
  }

  /** @returns {HTMLImageElement | null} */
  getMainProductImage() {
    if (!this.sectionRoot) return null;
    const gallery = this.sectionRoot.querySelector('media-gallery');
    if (!gallery) return null;
    return gallery.querySelector('.product-media__image');
  }

  startImageOverrideObserver() {
    this.stopImageOverrideObserver();

    const gallery = this.sectionRoot?.querySelector('media-gallery');
    if (!gallery) return;

    this.imageObserver = new MutationObserver(() => {
      const img = this.getMainProductImage();
      if (!img) return;

      const desired = this.selectedColorDetail?.colorImage || '';
      const isActive = img.hasAttribute(COLOR_IMAGE_DATA.active);

      // If we have an active color image, keep enforcing it even if another script
      // updates the variant image after our variantUpdate handler runs.
      if (desired && isActive && img.getAttribute('src') !== desired) {
        this.applyColorImage();
      }
    });

    this.imageObserver.observe(gallery, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'class'],
    });
  }

  stopImageOverrideObserver() {
    if (this.imageObserver) {
      this.imageObserver.disconnect();
      this.imageObserver = null;
    }

    if (this.reapplyTimeoutId) {
      window.clearTimeout(this.reapplyTimeoutId);
      this.reapplyTimeoutId = null;
    }
  }

  applyColorImage() {
    if (!this.enableImageOverride) return;
    const colorImage = this.selectedColorDetail?.colorImage;
    if (!colorImage) return;

    const img = this.getMainProductImage();
    if (!img) return;

    if (!img.hasAttribute(COLOR_IMAGE_DATA.originalSrc)) {
      img.setAttribute(COLOR_IMAGE_DATA.originalSrc, img.getAttribute('src') || '');
      img.setAttribute(COLOR_IMAGE_DATA.originalSrcset, img.getAttribute('srcset') || '');
    }

    img.src = colorImage;
    img.removeAttribute('srcset');
    img.setAttribute(COLOR_IMAGE_DATA.active, '');
  }

  restoreOriginalImage() {
    if (!this.enableImageOverride) return;
    const img = this.getMainProductImage();
    if (!img || !img.hasAttribute(COLOR_IMAGE_DATA.originalSrc)) return;

    const originalSrc = img.getAttribute(COLOR_IMAGE_DATA.originalSrc);
    const originalSrcset = img.getAttribute(COLOR_IMAGE_DATA.originalSrcset);

    if (originalSrc) img.setAttribute('src', originalSrc);
    if (originalSrcset) img.setAttribute('srcset', originalSrcset);

    img.removeAttribute(COLOR_IMAGE_DATA.originalSrc);
    img.removeAttribute(COLOR_IMAGE_DATA.originalSrcset);
    img.removeAttribute(COLOR_IMAGE_DATA.active);
  }

  triggerOptionChange(position, valueToSelect) {
    const picker = this.getVariantPicker();
    if (!picker) return;

    const valueLower = valueToSelect.toLowerCase();
    const inputs = Array.from(picker.querySelectorAll(`[data-input-id^="${position}-"]`));
    
    /** @type {HTMLInputElement | HTMLOptionElement | null} */
    let targetInput = null;

    for (const el of inputs) {
      const input = /** @type {HTMLInputElement | HTMLOptionElement} */ (el);
      if (input.value && input.value.toLowerCase() === valueLower) {
        targetInput = input;
        break;
      }
    }

    if (!targetInput) return;

    if (targetInput instanceof HTMLInputElement) {
      if (targetInput.checked) return;
      targetInput.checked = true;
      this.pendingVariantChange = true;
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (targetInput instanceof HTMLOptionElement) {
      const select = targetInput.closest('select');
      if (select && select.value !== targetInput.value) {
        select.value = targetInput.value;
        this.pendingVariantChange = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  handleColorSelection(event) {
    const detail = event.detail;
    if (!detail || !detail.colorBase) return;

    this.selectedColorDetail = detail;

    if (this.enableImageOverride) {
      if (detail.colorImage) {
        this.applyColorImage();
      } else {
        this.restoreOriginalImage();
      }
    }

    const requiredBase = detail.colorBase.toLowerCase();
    
    const currentCanSize = this.getSelectedValue(TINT_VARIANT_POSITIONS.canSize) || '';
    const currentTint = this.getSelectedValue(TINT_VARIANT_POSITIONS.tint) || '';
    const variants = this.getVariantsMatrix();
    
    const exactVariant = variants.find((/** @type {ProductVariant} */ v) =>
      (v.option1 || '').toLowerCase() === currentCanSize.toLowerCase() &&
      (v.option3 || '').toLowerCase() === currentTint.toLowerCase() &&
      (v.option2 || '').toLowerCase() === requiredBase
    );

    if (exactVariant && exactVariant.available) {
      this.triggerOptionChange(2, exactVariant.option2);
    } else {
      this.selectedColorDetail = null;
      this.clearColorPickerUI();
      this.restoreOriginalImage();
      this.updateAddToCartButtonState();
    }
    
    this.updateAddToCartButtonState();
  }

  handleColorCleared(event) {
    this.selectedColorDetail = null;
    if (this.enableImageOverride) this.restoreOriginalImage();
    this.updateAddToCartButtonState();
  }

  handleChange(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || !target.closest('variant-picker')) return;

    let isTint = false;

    if (target instanceof HTMLInputElement) {
      const inputId = target.getAttribute('data-input-id');
      if (inputId && inputId.startsWith(`${TINT_VARIANT_POSITIONS.tint}-`)) {
        isTint = true;
      }
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      if (selectedOption) {
        const inputId = selectedOption.getAttribute('data-input-id');
        if (inputId && inputId.startsWith(`${TINT_VARIANT_POSITIONS.tint}-`)) {
          isTint = true;
        }
      }
    }

    if (isTint && (target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) {
      const selectedValue = target.value.toLowerCase();
      this.updateColorPickerVisibility();
      
      if (this.isUndyedTintValue(selectedValue)) {
        this.selectedColorDetail = null;
        this.clearColorPickerUI();
        if (this.enableImageOverride) this.restoreOriginalImage();
        this.triggerOptionChange(TINT_VARIANT_POSITIONS.base, TINT_VARIANT_BASE_VALUES.undyedDefault);
      }
      
      this.updateAddToCartButtonState();
    }
  }

  clearColorPickerUI() {
    if (!this.pickerRoot) return;
    
    const clearEvent = new CustomEvent(TINT_VARIANT_EVENTS.colorCleared, { bubbles: true });
    this.pickerRoot.dispatchEvent(clearEvent);
    
    try {
      const clearBtn = /** @type {HTMLElement} */ (this.pickerRoot.querySelector('.tint-color-picker__clear'));
      if (clearBtn && !clearBtn.hidden) {
        clearBtn.click();
      }
    } catch (error) {
      // Suppressed
    }
  }

  handleVariantUpdate(event) {
    const { resource } = event.detail || {};
    if (!resource) return;

    this.pendingVariantChange = false;

    const currentTint = this.getSelectedValue(TINT_VARIANT_POSITIONS.tint) || '';
    this.updateColorPickerVisibility();
    
    if (this.isDyedTintValue(currentTint) && this.selectedColorDetail) {
      const img = this.enableImageOverride ? this.getMainProductImage() : null;

      if (img && !img.hasAttribute(COLOR_IMAGE_DATA.active)) {
        img.removeAttribute(COLOR_IMAGE_DATA.originalSrc);
        img.removeAttribute(COLOR_IMAGE_DATA.originalSrcset);
      }

      if (this.enableImageOverride && this.selectedColorDetail.colorImage) {
        // Some product-media code updates the main image after variantUpdate; re-apply shortly after.
        this.applyColorImage();
        this.reapplyTimeoutId = window.setTimeout(() => {
          this.reapplyTimeoutId = null;
          this.applyColorImage();
        }, 0);
      }

      if (this.pickerRoot) {
        const confirmEvent = new CustomEvent(TINT_VARIANT_EVENTS.colorConfirmed, {
          bubbles: true,
          detail: this.selectedColorDetail,
        });
        this.pickerRoot.dispatchEvent(confirmEvent);
      }
    } else if (!this.isDyedTintValue(currentTint)) {
      this.selectedColorDetail = null;
      this.clearColorPickerUI();
      if (this.enableImageOverride) this.restoreOriginalImage();
    }
    
    this.updateAddToCartButtonState();
  }
}

customElements.define('color-variant-manager', ColorVariantManager);